import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { b64toBlob } from '_utils';
import ImageService from '_services/rest/modules/image';
import config from '_config';
import i18n from '@/i18n';

export class ExportManager {
  static async processExport({
    items, startIndex = 0, gameType, configMap, formatMode, exportOptions,
    selectedTypes, prefix, hooks,
  }) {
    const { showLoader, showError } = hooks;
    const gameConfig = configMap[gameType];
    const zip = new JSZip();
    const svgFiles = [];

    items.forEach((item, index) => {
      const globalIndex = startIndex + index + 1;
      const useIndex = gameConfig.useIndexNaming;
      const baseName = useIndex ? `${globalIndex}` : item.name;

      Object.keys(selectedTypes).forEach(typeKey => {
        if (!selectedTypes[typeKey]) return;

        const typeConfig = gameConfig.types[typeKey];

        if (typeConfig.isText) {
          const content = typeof typeConfig.generateText === 'function'
            ? typeConfig.generateText(item)
            : (item.highlightedWords?.map(w => w.word)
              .sort((a, b) => a.localeCompare(b)) || [])
              .join('\n');

          zip.file(
            `${baseName}${typeConfig.suffix}.txt`,
            new Blob([content], { type: 'text/plain' }),
          );
          return;
        }

        const svg = gameConfig.generateSvg(item, typeKey, exportOptions);
        if (svg) {
          svgFiles.push({ id: `${baseName}${typeConfig.suffix}`, svg });
        }
      });
    });

    if (svgFiles.length === 0 && Object.keys(zip.files).length === 0) {
      showError(i18n.t('shared.export.noFiles'));
      return false;
    }

    if (formatMode === 'eps' && svgFiles.length > 0) {
      const batchLimit = config.SVG_UPLOAD_LIMIT || 10;
      let nextProgress = 0;

      for (let i = 0; i < svgFiles.length; i += batchLimit) {
        const batch = svgFiles.slice(i, i + batchLimit);
        nextProgress += batch.length;

        showLoader(i18n.t('shared.export.converting', {
          current: nextProgress,
          total: svgFiles.length,
        }));

        try {
          // eslint-disable-next-line no-await-in-loop
          const convertedFiles = await ImageService.convertMultipleSVGsToEPS(batch);
          // eslint-disable-next-line no-restricted-syntax
          for (const file of convertedFiles) {
            if (file.epsBase64) {
              zip.file(`${file.id}.eps`, b64toBlob(file.epsBase64, 'application/postscript'));
            } else {
              throw new Error(`Erro ao converter ${file.id}: ${file.error}`);
            }
          }
        } catch (error) {
          showError(i18n.t('shared.export.conversionError'));
          return false;
        }
      }
    } else {
      svgFiles.forEach(file => zip.file(`${file.id}.svg`, file.svg));
    }

    showLoader(i18n.t('shared.export.finishing'));
    const filesCount = Object.keys(zip.files).length;

    if (filesCount === 1) {
      const fileName = Object.keys(zip.files)[0];
      const blob = await zip.files[fileName].async('blob');
      saveAs(blob, fileName);
    } else {
      const blob = await zip.generateAsync({ type: 'blob' });
      saveAs(blob, `${prefix}.zip`);
    }

    return true;
  }
}
